/**
 * @copyright WaterStreet. All rights reserved.
 */

/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable max-len */

import {
	Component,
	computed,
	DoCheck,
	input,
	InputSignal,
	output,
	OutputEmitterRef,
	Signal
} from '@angular/core';
import {
	DrawerAnimation
} from '@shared/app-animations';
import {
	AnyHelper
} from '@shared/helpers/any.helper';
import {
	IDrawerMenuItem
} from '@shared/interfaces/application-objects/drawer-menu-item.interface';
import {
	IDynamicComponentContext
} from '@shared/interfaces/application-objects/dynamic-component-context.interface';

/* eslint-enable max-len */

@Component({
	selector: 'app-drawers',
	templateUrl: './app-drawers.component.html',
	styleUrls: ['./app-drawers.component.scss'],
	animations: [
		DrawerAnimation
	]
})

/**
 * A component representing an instance of a drawer displayed in the
 * drawers component.
 *
 * @export
 * @class AppDrawersComponent
 */
export class AppDrawersComponent implements DoCheck
{
	/**
	 * Gets the current set of pinned drawer items.
	 *
	 * @type {InputSignal<IDrawerMenuItem[]>}
	 * @memberof AppDrawersComponent
	 */
	public readonly pinnedDrawerItems: InputSignal<IDrawerMenuItem[]> =
		input<IDrawerMenuItem[]>([]);

	/**
	 * Gets the current overlayed drawer item.
	 *
	 * @type {InputSignal<IDrawerMenuItem | null>}
	 * @memberof AppDrawersComponent
	 */
	public readonly overlayDrawerItem: InputSignal<IDrawerMenuItem | null> =
		input<IDrawerMenuItem | null>(null);

	/**
	 * Gets the page context used for linking drawer displays to
	 * page content.
	 *
	 * @type {InputSignal<IDynamicComponentContext<Component, any>>}
	 * @memberof AppDrawersComponent
	 */
	public readonly pageContext:
		InputSignal<IDynamicComponentContext<Component, any>> =
			input.required<IDynamicComponentContext<Component, any>>();

	/**
	 * Gets the event emitter that fires when a pin drawer button is clicked.
	 *
	 * @type {OutputEmitterRef<IDrawerMenuItem>}
	 * @memberof AppDrawersComponent
	 */
	public readonly drawerPinButtonClicked: OutputEmitterRef<IDrawerMenuItem> =
		output<IDrawerMenuItem>();

	/**
	 * Gets the event emitter that fires when a close drawer button is clicked.
	 *
	 * @type {OutputEmitterRef<IDrawerMenuItem>}
	 * @memberof AppDrawersComponent
	 */
	public readonly drawerCloseButtonClicked:
		OutputEmitterRef<IDrawerMenuItem> =
			output<IDrawerMenuItem>();

	/**
	 * Gets the combined list of active drawers for unified rendering. The
	 * overlay item (if present and not already pinned) is prepended so that
	 * when the user pins it, pinnedDrawerItems prepends it too — the array
	 * order is unchanged and trackByLabel reuses the existing component
	 * instance, preserving all state.
	 *
	 * @type {Signal<IDrawerMenuItem[]>}
	 * @memberof AppDrawersComponent
	 */
	public readonly allActiveDrawers: Signal<IDrawerMenuItem[]> =
		computed<IDrawerMenuItem[]>(() =>
		{
			if (AnyHelper.isNull(this.overlayDrawerItem()))
			{
				return this.pinnedDrawerItems();
			}

			const overlayItem: IDrawerMenuItem =
 				this.overlayDrawerItem();

			const isAlreadyPinned: boolean =
				overlayItem.isGlobal === true
 					? this.pinnedDrawerItems().some(
 						(item: IDrawerMenuItem) =>
 							item.isGlobal === true
 								&& item.label === overlayItem.label)
 					: this.pinnedDrawerItems().includes(overlayItem);

			return isAlreadyPinned
				? this.pinnedDrawerItems()
				: [
					overlayItem,
					...this.pinnedDrawerItems()
				];
		});

	/**
	 * Tracks the last overlay item to detect when the overlay drawer switches
	 * from one non-null item to another so the animation state can suppress
	 * the slide-in.
	 *
	 * @type {IDrawerMenuItem | null}
	 * @memberof AppDrawersComponent
	 */
	private lastOverlayItem: IDrawerMenuItem | null = null;

	/**
	 * Indicates whether the overlay drawer is currently
	 * switching between items.
	 *
	 * @type {boolean}
	 * @memberof AppDrawersComponent
	 */
	private isSwitchingOverlay: boolean = false;

	/**
	 * Detects when the overlay drawer switches from one non-null item to
	 * another so the animation state can suppress the slide-in.
	 *
	 * @memberof AppDrawersComponent
	 */
	public ngDoCheck(): void
	{
		const current: IDrawerMenuItem | null =
			this.overlayDrawerItem();
		this.isSwitchingOverlay =
			!AnyHelper.isNull(this.lastOverlayItem)
				&& !AnyHelper.isNull(current)
				&& this.lastOverlayItem !== current;

		this.lastOverlayItem = current;
	}

	/**
	 * Returns the animation state for the given drawer wrapper. Overlay items
	 * use the 'switch' state (instant, no slide) when switching between open
	 * overlay drawers; otherwise the default 'false' state is used so the
	 * normal slide-in animation plays on first open.
	 *
	 * @param {IDrawerMenuItem} drawerMenuItem
	 * The drawer menu item whose animation state to resolve.
	 * @returns {string}
	 * The drawer animation state for the given drawer wrapper.
	 * @memberof AppDrawersComponent
	 */
	public getDrawerAnimationState(
		drawerMenuItem: IDrawerMenuItem): string
	{
		return this.isOverlayItem(drawerMenuItem)
			&& this.isSwitchingOverlay
			? 'switch'
			: 'false';
	}

	/**
	 * Handles the close drawer click by bubbling this event up to the
	 * containing command component.
	 *
	 * @param {IDrawerMenuItem} drawerMenuItem
	 * The menu item that received the close click event.
	 * @memberof AppDrawersComponent
	 */
	public closeDrawerClick(
		drawerMenuItem: IDrawerMenuItem): void
	{
		this.drawerCloseButtonClicked.emit(
			drawerMenuItem);
	}

	/**
	 * Handles the pin drawer click by bubbling this event up to the containing
	 * command component.
	 *
	 * @param {IDrawerMenuItem} drawerMenuItem
	 * The menu item that received the pin click event.
	 * @memberof AppDrawersComponent
	 */
	public pinDrawerClick(
		drawerMenuItem: IDrawerMenuItem): void
	{
		this.drawerPinButtonClicked.emit(
			drawerMenuItem);
	}

	/**
	 * Returns true when the given drawer menu item is currently the overlay
	 * (floating) item.
	 *
	 * @param {IDrawerMenuItem} drawerMenuItem
	 * The drawer menu item to evaluate.
	 * @returns {boolean}
	 * @memberof AppDrawersComponent
	 */
	public isOverlayItem(
		drawerMenuItem: IDrawerMenuItem): boolean
	{
		return drawerMenuItem === this.overlayDrawerItem();
	}

	/**
	 * Returns the CSS offset class for the overlay drawer based on the number
	 * of currently pinned drawers, or null for pinned items.
	 *
	 * @param {IDrawerMenuItem} drawerMenuItem
	 * The drawer menu item to evaluate.
	 * @returns {string | null}
	 * @memberof AppDrawersComponent
	 */
	public getOverlayOffsetClass(
		drawerMenuItem: IDrawerMenuItem): string | null
	{
		if (!this.isOverlayItem(drawerMenuItem))
		{
			return null;
		}

		const offsetLabels: string[] =
			['', 'one', 'two', 'three', 'four', 'five'];
		const pinnedCount: number =
			this.pinnedDrawerItems().length;

		return pinnedCount > 0 && pinnedCount <= 5
			? `drawer-${offsetLabels[pinnedCount]}-open-overlay`
			: null;
	}

	/**
	 * Tracks pinned drawer items to prevent unnecessary DOM recreation
	 * for global utilities on navigation. Global drawers are tracked by
	 * label (stable identity), while context drawers use object identity
	 * so they reload with fresh data on each page navigation.
	 *
	 * @param {number} _index
	 * The index of the item in the array.
	 * @param {IDrawerMenuItem} drawerMenuItem
	 * The drawer menu item to track.
	 * @returns {string | IDrawerMenuItem}
	 * The stable label for global drawers, or the object itself for context
	 * drawers so Angular recreates them with current instance data.
	 * @memberof AppDrawersComponent
	 */
	public trackByLabel(
		_index: number,
		drawerMenuItem: IDrawerMenuItem): string | IDrawerMenuItem
	{
		return drawerMenuItem.isGlobal === true
			? drawerMenuItem.label
			: drawerMenuItem;
	}
}